import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'pages/APIs';
import type {
  DiscordIntegration,
  DiscordIntegrationCreate,
  DiscordIntegrationUpdate,
  DiscordListResponse,
  DiscordSingleResponse,
  DiscordTestResponse,
  DiscordDeleteResponse,
} from 'src/@types/discord';

/**
 * API Service for managing Discord channel integrations via doc2-api.
 */
export default class DiscordApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  /**
   * List all Discord integrations for the organization.
   */
  public async list(): Promise<DiscordListResponse> {
    return super.get(api.discord_list);
  }

  /**
   * Get a single Discord integration by ID.
   */
  public async getOne(id: string): Promise<DiscordSingleResponse> {
    return super.get(`${api.discord_get}${id}`);
  }

  /**
   * Create a new Discord integration.
   */
  public async create(
    data: DiscordIntegrationCreate
  ): Promise<DiscordSingleResponse> {
    return super.post(api.discord_create, data);
  }

  /**
   * Update an existing Discord integration.
   */
  public async update(
    id: string,
    data: DiscordIntegrationUpdate
  ): Promise<DiscordSingleResponse> {
    return super.put(`${api.discord_update}${id}`, data);
  }

  /**
   * Delete a Discord integration.
   */
  public async remove(id: string): Promise<DiscordDeleteResponse> {
    return super.delete(`${api.discord_delete}${id}`, {});
  }

  /**
   * Test a Discord integration by sending a test message.
   */
  public async test(id: string): Promise<DiscordTestResponse> {
    return super.post(`${api.discord_test}${id}/test`, {});
  }

  // =====================================================
  // Alias methods for consistency
  // =====================================================

  /**
   * Get all Discord integrations (alias for list, returns array).
   */
  public async getAll(): Promise<DiscordIntegration[]> {
    const result = await this.list();
    return result.data || [];
  }

  /**
   * Get integration by ID (alias for getOne).
   */
  public async getById(id: string): Promise<DiscordIntegration> {
    const result = await this.getOne(id);
    return result.data;
  }

  /**
   * Delete integration (alias for remove).
   */
  public async deleteIntegration(id: string): Promise<void> {
    await this.remove(id);
  }

  /**
   * Toggle integration enabled status.
   */
  public async toggle(
    id: string,
    enabled: boolean
  ): Promise<DiscordSingleResponse> {
    return this.update(id, { is_enabled: enabled });
  }
}
